import Spine from '@finviz/spine'
import { isSameDay, isSameMonth, isSameWeek } from 'date-fns'
import omit from 'lodash.omit'
import queryString from 'query-string'

import { Instrument, ObjectHash, RootChartConfigObject, TodoObjectHashAnyType } from '../../../types/shared'
import { IntradayTimeframeInterval, QuoteFinancialAttachmentType, TIMEFRAME } from '../../constants/common'
import type { ICOT } from '../../indicators/cot'
import { convertLocalToNyTime, dateFromUnixTimestamp, dateStringFromDate } from '../../utils'
import { getIsAbortError } from '../../utils/abort_controller'
import { getPositionXFromTimestamp, getTimestampFromPositionX } from '../../utils/chart'
import { IDividends, IEarnings, ISplit } from '../../utils/chart-events-utils'
import fetchApi, { NotFoundError } from '../../utils/fetch_api'
import { getIsSSr } from '../../utils/helpers'
import { deleteCachedQuote, getCachedQuote, setCachedQuote } from '../quoteCache'
import { getRawTickerForInstrument } from '../utils'
import { FUTURES_FORCE_REFETCH_TIMEOUT, QuoteFetchOptions, QuoteFetchType, QuoteUrlOptions } from './constants'
import { FinancialAttachmentsData } from './financialAttachmentsData'

type TodoPattern = TodoObjectHashAnyType

interface QuoteInternalOptions {
  cachePredicate?: (quote: Quote) => boolean
  fetchNewDataOnCachedQuote?: boolean
}

interface UrlOptions extends Omit<QuoteFetchOptions, 'financialAttachments'> {
  ticker: string
  instrument: string
  timeframe: string
  financialAttachments?: string
  dataId?: string
}

type ChartEventDataRaw = IEarnings | IDividends | ISplit

export interface DataResponse {
  COTs: COTData
  name: string
  ticker: string
  instrument: string
  interval: number
  timeframe: string
  open: number[]
  high: number[]
  low: number[]
  close: number[]
  date: number[]
  volume: number[]
  lastOpen: number
  lastHigh: number
  lastLow: number
  lastClose: number
  lastDate: number
  lastTime: string | null
  lastVolume: number
  dataId: string
  prevClose: number
  afterClose: number
  afterChange: number
  afterTime: string | null
  drawMinutesPerDay: number
  marketStartMinutes: number
  premarketLengthMinutes: number
  aftermarketLengthMinutes: number
  aftermarket: boolean
  premarket: boolean
  hasPatterns: boolean
  patterns: Array<TodoPattern>
  patternsMinRange: number
  patternsMaxRange: number
  relativeVolume: number
  isIntraday: boolean
  noNewData?: boolean
  updateOhlcVersion?: number
  chartEvents?: ChartEventDataRaw[]
  financialAttachmentsData?: FinancialAttachmentsData
}

interface COTData {
  [code: string]: ICOT
}

const COTProps = ['commercialHedgers', 'largeTraders', 'smallTraders'] as const

const getEmptyQuote = () => ({
  open: [],
  high: [],
  low: [],
  close: [],
  volume: [],
  date: [],
  fetchedAt: -1,
  lastRefetch: -1,
  fetchingState: [],
  is404: false,
  hasError: false,
  COTs: undefined,
  name: undefined,
  drawMinutesPerDay: undefined,
  marketStartMinutes: undefined,
  premarketLengthMinutes: undefined,
  aftermarketLengthMinutes: undefined,
  patterns: [],
  patternsMinRange: undefined,
  patternsMaxRange: undefined,
  relativeVolume: undefined,
  lastOpen: undefined,
  lastHigh: undefined,
  lastLow: undefined,
  lastClose: undefined,
  lastVolume: undefined,
  dataId: undefined,
  lastDate: undefined,
  prevClose: undefined,
  afterClose: undefined,
  afterChange: undefined,
  chartEvents: [],
  financialAttachments: [],
  financialAttachmentsData: null,
  hasReachedEnd: false,
})

interface ICacheAvailabilityCheckFunctionsArguments {
  quote: Quote
  options?: QuoteFetchOptions & QuoteInternalOptions
}

const doesQuoteAndOptionsMatch = ({ quote, options }: ICacheAvailabilityCheckFunctionsArguments) =>
  // Only intraday quotes have afterhours
  (!quote.isIntraday || (!quote.premarket === !options?.premarket && !quote.aftermarket === !options?.aftermarket)) &&
  (!options?.patterns || !quote.hasPatterns === !options?.patterns) &&
  // if options.events is false we can reuse quote with events
  (!options?.events || !!quote.events) &&
  // Either has no financialAttachments or require all of them to match
  ((options?.financialAttachments?.length ?? 0) === 0 ||
    options!.financialAttachments!.every((financialAttachment) =>
      quote.financialAttachments?.includes(financialAttachment)
    ))

const doesQuoteAndFetchParamsMatch = ({
  quote,
  ticker,
  instrument,
  timeframe,
}: {
  quote: Quote
  ticker: string
  instrument: Instrument
  timeframe: TIMEFRAME
}) => quote.ticker === ticker && quote.instrument === instrument && quote.timeframe === timeframe

// either we don't care about matching uuid at all, or they have to match
const isSameOrNoChartUuid = ({ quote, uuid }: { quote: Quote; uuid?: string }) => !uuid || quote.chartUuid === uuid

const isCacheAllowed = ({ quote, options }: ICacheAvailabilityCheckFunctionsArguments) =>
  !options?.cachePredicate || options.cachePredicate(quote)

const isNotEmptyQuote = (quote: Quote) => quote.fetchedAt !== -1 || quote.getIsFetching() || !!quote.is404

export type QuoteGetParams = {
  ticker: string
  instrument: Instrument
  timeframe: TIMEFRAME
  options?: QuoteFetchOptions & QuoteInternalOptions
  abortController?: null | AbortController
  shouldUseCache?: boolean
  canBeEmptyQuote?: boolean
  chartUuid?: string
}

function getChartEventsWithId({
  ticker,
  chartEvents,
  updateOhlcVersion,
}: {
  ticker: string
  chartEvents?: ChartEventDataRaw[]
  updateOhlcVersion?: number
}) {
  return (
    chartEvents?.map((item) => ({
      ...item,
      elementId: `${ticker.toLowerCase()}-${item.eventType}-${item.dateTimestamp}`,
      updateOhlcVersion,
    })) ?? []
  )
}

class Quote extends Spine.Model {
  static computableKeys = [
    'isIntraday',
    'interval',
    'drawDays',
    'barIndex',
    'barToDataIndex',
    'alignedFinancialAttachmentsData',
    'timestampToFinancialsIndexesMap',
  ]

  static initClass() {
    this.configure(
      'Quote',
      'ticker',
      'name',
      'instrument',
      'timeframe',
      'open',
      'high',
      'low',
      'close',
      'date',
      'volume',
      'lastOpen',
      'lastHigh',
      'lastLow',
      'lastClose',
      'lastDate',
      'lastVolume',
      'lastTime',
      'dataId',
      'prevClose',
      'COTs',
      'chartEvents',
      'afterClose',
      'afterChange',
      'afterTime',
      'drawMinutesPerDay',
      'marketStartMinutes',
      'premarketLengthMinutes',
      'aftermarketLengthMinutes',
      'aftermarket',
      'premarket',
      'events',
      'financialAttachments',
      'financialAttachmentsData',
      'hasPatterns',
      'patterns',
      'patternsMinRange',
      'patternsMaxRange',
      'relativeVolume',
      'fetchedAt',
      'fetchingPromise',
      'fetchingState',
      'wasFetchAborted',
      'chartUuid',
      'ideaID',
      'updateOhlcVersion',
      'lastRefetch',
      'is404',
      'hasError',
      'hasReachedEnd',
      ...Quote.computableKeys
    )
  }

  static getFromCacheSync({
    ticker: tickerRaw,
    instrument,
    timeframe,
    options,
    chartUuid,
    canBeEmptyQuote,
  }: Omit<QuoteGetParams, 'abortController' | 'shouldUseCache'>): Quote | null {
    const ticker = tickerRaw.replace('@', '')
    return (
      Quote.select<Quote>(
        (q) =>
          doesQuoteAndFetchParamsMatch({ quote: q, ticker, instrument, timeframe }) &&
          isSameOrNoChartUuid({ quote: q, uuid: chartUuid }) &&
          (canBeEmptyQuote || isNotEmptyQuote(q)) &&
          isCacheAllowed({ quote: q, options }) &&
          (q.instrument !== Instrument.Stock ||
            Number.isInteger(q.ideaID) ||
            doesQuoteAndOptionsMatch({ quote: q, options }))
      )[0] ?? null
    )
  }

  static async get({
    ticker: tickerRaw,
    instrument,
    timeframe,
    options,
    abortController,
    shouldUseCache = true,
    canBeEmptyQuote = false,
    chartUuid,
  }: QuoteGetParams): Promise<Quote> {
    const ticker = tickerRaw.replace('@', '')
    const quote = shouldUseCache
      ? this.getFromCacheSync({ ticker: tickerRaw, instrument, timeframe, options, chartUuid, canBeEmptyQuote })
      : null

    if (quote) {
      if (quote.getIsFetching()) {
        await quote.getResolvedFetchingPromise()
      } else if (options?.fetchNewDataOnCachedQuote) {
        await quote.fetchData({ fetchType: QuoteFetchType.NewerData, abortController })
      }
      return quote
    }

    const newQuote = Quote.create<Quote>({
      ...getEmptyQuote(),
      ticker,
      timeframe,
      instrument,
      chartUuid,
      premarket: !!options?.premarket,
      aftermarket: !!options?.aftermarket,
      hasPatterns: !!options?.patterns,
      events: !!options?.events,
      financialAttachments: options?.financialAttachments ?? [],
    })

    await newQuote.fetchData({
      fetchType: QuoteFetchType.Refetch,
      abortController,
      options: {
        dateFrom: options?.dateFrom,
        dateTo: options?.dateTo,
        barsCount: options?.barsCount,
        leftBuffer: options?.leftBuffer,
      },
    })

    return newQuote
  }

  static async getAll(
    tickersAndTimeframes: Array<Pick<QuoteGetParams, 'ticker' | 'instrument' | 'timeframe' | 'options'>>
  ) {
    // TODO: fetch in 1 request
    if (tickersAndTimeframes.length === 0) {
      return {}
    }

    try {
      const quotes = await Promise.all(tickersAndTimeframes.map((props) => this.get(props)))
      const data = quotes.reduce(
        (accumulator, quote) => ({
          ...accumulator,
          [quote.ticker]: quote,
        }),
        {}
      )
      return data
    } catch {
      // TODO: handle somehow
    }
  }

  declare ticker: string
  declare name: string
  declare instrument: Instrument
  declare interval: number
  declare timeframe: TIMEFRAME
  declare open: number[]
  declare high: number[]
  declare low: number[]
  declare close: number[]
  declare date: number[]
  declare volume: number[]
  declare lastOpen: number
  declare lastHigh: number
  declare lastLow: number
  declare lastClose: number
  declare lastDate: number
  declare lastVolume: number
  declare dataId: string
  declare prevClose: number
  declare COTs: COTData
  declare afterClose: number
  declare afterChange: number
  declare drawMinutesPerDay: number
  declare marketStartMinutes: number
  declare premarketLengthMinutes: number
  declare aftermarketLengthMinutes: number
  declare hasPatterns: boolean
  declare patterns: Array<TodoPattern>
  declare patternsMinRange: number
  declare patternsMaxRange: number
  declare relativeVolume: number
  declare isIntraday: boolean
  declare hasReachedEnd: boolean
  declare drawDays?: number
  declare barIndex: number[]
  /**
   * Array of length the same as number of bars
   *
   * If there's a gap and a bar index has no corresponding data index,
   * the last data index is used (or 0 if there's no last data index)
   *
   * @type {number[]}
   */
  declare barToDataIndex: number[]
  declare chartUuid?: string
  declare aftermarket: boolean
  declare premarket: boolean
  declare fetchedAt: number
  declare fetchingState: QuoteFetchType[]
  declare fetchingPromise?: Promise<void>
  declare wasFetchAborted: boolean
  declare ideaID?: number
  declare updateOhlcVersion?: number
  declare lastRefetch: number
  declare is404?: boolean
  declare hasError?: boolean
  declare chartEvents: Array<IEarnings | IDividends | ISplit>
  declare events?: boolean
  declare financialAttachments?: QuoteFinancialAttachmentType[]
  declare financialAttachmentsData?: FinancialAttachmentsData
  declare alignedFinancialAttachmentsData: FinancialAttachmentsData

  declare timestampToFinancialsIndexesMap: Partial<ObjectHash<ObjectHash<number[]>, QuoteFinancialAttachmentType>>

  async fetchRequest({
    fetchType,
    abortController,
    options = {},
  }: {
    fetchType: QuoteFetchType
    abortController?: null | AbortController
    options?: QuoteUrlOptions
  }) {
    let data: DataResponse | null = null
    let is404 = false
    let hasError = false
    let wasFetchAborted = false

    const fetchingPromise = fetchApi<DataResponse>({
      location: this.getUrl({ dataId: fetchType === QuoteFetchType.NewerData ? this.dataId : undefined, ...options }),
      throwOnStatusCodes: [404],
      abortController,
    })

    this.updateAttributes({
      fetchingState: [...this.fetchingState, fetchType],
      fetchingPromise,
    })

    try {
      data = await fetchingPromise
    } catch (err) {
      is404 = err instanceof NotFoundError
      if (getIsAbortError(err as Error)) {
        wasFetchAborted = true
      } else {
        hasError = true
        if (!is404 && process.env.IS_E2E_TESTING) {
          throw new Error('Quote fetch error', { cause: err })
        }
      }
      // Ignore network/notfound errors
    }

    const shouldUseEmptyQuote = !data && !wasFetchAborted && fetchType === QuoteFetchType.Refetch

    this.updateAttributes({
      ...(shouldUseEmptyQuote ? getEmptyQuote() : {}),
      is404,
      hasError,
      wasFetchAborted,
      fetchingState: this.fetchingState.filter((type) => type !== fetchType),
    })

    return data
  }

  getIsFetching(fetchType?: QuoteFetchType | QuoteFetchType[]) {
    if (fetchType === undefined) return this.fetchingState.length > 0

    const fetchTypeArray = Array.isArray(fetchType) ? fetchType : [fetchType]
    return this.fetchingState.some((type) => fetchTypeArray.includes(type))
  }

  async isPossibleToFetchSequentialData({
    abortController,
  }: {
    abortController?: null | AbortController
  } = {}) {
    const data = await this.fetchRequest({
      fetchType: QuoteFetchType.NewerData,
      abortController,
      options: {
        dateFrom: this.date[this.date.length - 2],
        barsCount: 1,
        dataId: undefined,
      },
    })
    if (data && data.date.length > 0 && this.date.length > 0) {
      return this.date[this.date.length - 1] >= data.date[0]
    }
    return false
  }

  async fetchData({
    fetchType,
    abortController,
    options = {},
  }: {
    fetchType: QuoteFetchType
    abortController?: null | AbortController
    options?: QuoteUrlOptions
  }): Promise<void> {
    const isFuturesForcedRefetch =
      fetchType === QuoteFetchType.NewerData &&
      this.instrument === Instrument.Futures &&
      this.lastRefetch < Date.now() - FUTURES_FORCE_REFETCH_TIMEOUT
    const isRefetch = [QuoteFetchType.Refetch, QuoteFetchType.DataChangeRefetch].includes(fetchType)

    if (isFuturesForcedRefetch) {
      return this.dataChangeRefetch(abortController)
    } else if (isRefetch) {
      options.dateFrom = options.dateFrom ?? this.date[0]
    } else if (fetchType === QuoteFetchType.NewerData) {
      if (this.date.length >= 2) {
        options.dateFrom = this.date[this.date.length - 2]
      }
    } else if (fetchType === QuoteFetchType.Older) {
      options.dateTo = this.date[0]
    }

    const data = await this.fetchRequest({ fetchType, abortController, options })

    if (!data || data.noNewData === true) {
      return
    }
    if (!isRefetch && this.instrument === Instrument.Stock && this.updateOhlcVersion !== data.updateOhlcVersion) {
      return this.dataChangeRefetch(abortController)
    }
    if (fetchType === QuoteFetchType.Older && !data?.date.some((date) => date < this.date[0])) {
      this.updateAttributes({ hasReachedEnd: true })
      return
    }
    if (this.timeframe !== data.timeframe || this.ticker.toLowerCase() !== data.ticker?.toLowerCase()) {
      return
    }

    const newQuoteData = {
      COTs: isRefetch ? data.COTs : this.COTs,
      name: data.name,
      open: data.open,
      high: data.high,
      low: data.low,
      close: data.close,
      volume: data.volume,
      date: data.date,
      drawMinutesPerDay: data.drawMinutesPerDay,
      marketStartMinutes: data.marketStartMinutes,
      premarketLengthMinutes: data.premarketLengthMinutes,
      aftermarketLengthMinutes: data.aftermarketLengthMinutes,
      patterns: data.patterns ?? [],
      patternsMinRange: data.patternsMinRange,
      patternsMaxRange: data.patternsMaxRange,
      relativeVolume: data.relativeVolume,
      lastOpen: data.lastOpen,
      lastHigh: data.lastHigh,
      lastLow: data.lastLow,
      lastClose: data.lastClose,
      lastVolume: data.lastVolume,
      dataId: fetchType === QuoteFetchType.NewerData || isRefetch ? data.dataId : this.dataId,
      lastDate: data.lastDate,
      lastTime: data.lastTime,
      prevClose: data.prevClose,
      afterClose: data.afterClose,
      afterChange: data.afterChange,
      afterTime: data.afterTime,
      numberOfRefreshChecks: 0,
      updateOhlcVersion: this.updateOhlcVersion,
      lastRefetch: isRefetch ? Date.now() : this.lastRefetch,
      chartEvents: isRefetch ? (data.chartEvents ?? []) : this.chartEvents,
      fetchedAt: Date.now(),
      financialAttachmentsData: isRefetch ? data.financialAttachmentsData : this.financialAttachmentsData,
    }

    if (!isRefetch) {
      /*
       * Merges chart events
       * Adds new chart events and overwrites chart events with new ones if they are from the same day
       */
      if ((data.chartEvents?.length ?? 0) > 0) {
        newQuoteData.chartEvents = [
          ...this.chartEvents.filter(
            ({ dateTimestamp, eventType }) =>
              !data.chartEvents?.some(
                (chartEvent) =>
                  isSameDay(dateFromUnixTimestamp(chartEvent.dateTimestamp), dateFromUnixTimestamp(dateTimestamp)) &&
                  chartEvent.eventType === eventType
              )
          ),
          ...(data.chartEvents ?? []),
        ]
      }
      /*
       * Merges financial attachments
       * Adds new financial attachments and overwrites financial attachments with new ones if they have the same timestamp
       */
      Object.keys(data.financialAttachmentsData ?? {}).forEach(
        <T extends keyof FinancialAttachmentsData>(keyString: string) => {
          const key = keyString as T
          const financialAttachment = data.financialAttachmentsData![key]

          if (!financialAttachment?.length) return

          newQuoteData.financialAttachmentsData ??= {}
          newQuoteData.financialAttachmentsData[key] = [
            ...(this.financialAttachmentsData?.[key]?.filter(
              ({ timestamp }) => !financialAttachment.some((item) => timestamp === item.timestamp)
            ) ?? []),
            ...financialAttachment,
          ].sort((item1, item2) => item1.timestamp - item2.timestamp) as FinancialAttachmentsData[T]
        }
      )
      /*
       * Merges COTs
       */
      const COTKeys = this.instrument === Instrument.Futures ? Object.keys(data.COTs) : []
      if (COTKeys.length > 0) {
        const isOlder = fetchType === QuoteFetchType.Older
        for (let i = isOlder ? data.date.length - 1 : 0; isOlder ? i >= 0 : i < data.date.length; isOlder ? i-- : i++) {
          const date = data.date[i]
          if (this.date.includes(date)) {
            continue
          }
          COTKeys.forEach((key) => {
            COTProps.forEach((prop) => {
              if (isOlder) {
                newQuoteData.COTs[key][prop].unshift(data.COTs[key][prop][i])
              } else {
                newQuoteData.COTs[key][prop].push(data.COTs[key][prop][i])
              }
            })
          })
        }
      }
      /*
       * Merges current and new data taking date as index
       */
      let AIndex = 0
      let BIndex = 0
      let mergeIndex = -1
      while (AIndex < this.date.length || BIndex < data.date.length) {
        const ADate = this.date[AIndex]
        const BDate = data.date[BIndex]
        if (BDate === undefined) {
          break
        } else if (ADate === BDate) {
          // Same date, update OHLCV
          this.open[AIndex] = data.open[BIndex]
          this.high[AIndex] = data.high[BIndex]
          this.low[AIndex] = data.low[BIndex]
          this.close[AIndex] = data.close[BIndex]
          this.volume[AIndex] = data.volume[BIndex]
          AIndex++
          BIndex++
        } else if (ADate > BDate || ADate === undefined) {
          // New bar for a date not yet in our dataset
          // If weekly or monthly timeframe & wasn't merged on AIndex
          if (mergeIndex === -1 && [TIMEFRAME.w, TIMEFRAME.m].includes(this.timeframe)) {
            const prevAIndex = AIndex - 1
            const prevDate = dateFromUnixTimestamp(this.date[prevAIndex])
            const newDate = dateFromUnixTimestamp(data.date[BIndex])
            // If merge bar for weekly is sameWeek or for monthly is sameMonth perform merge on AIndex
            if (
              (TIMEFRAME.w === this.timeframe && isSameWeek(prevDate, newDate, { weekStartsOn: 1 })) ||
              (TIMEFRAME.m === this.timeframe && isSameMonth(prevDate, newDate))
            ) {
              this.date[prevAIndex] = data.date[BIndex]
              this.open[prevAIndex] = data.open[BIndex]
              this.high[prevAIndex] = data.high[BIndex]
              this.low[prevAIndex] = data.low[BIndex]
              this.close[prevAIndex] = data.close[BIndex]
              this.volume[prevAIndex] = data.volume[BIndex]
              mergeIndex = prevAIndex
              // Incrementing BIndex only because we're mergin arrays on AIndex
              BIndex++
              continue
            }
          }
          this.date.splice(AIndex, 0, data.date[BIndex])
          this.open.splice(AIndex, 0, data.open[BIndex])
          this.high.splice(AIndex, 0, data.high[BIndex])
          this.low.splice(AIndex, 0, data.low[BIndex])
          this.close.splice(AIndex, 0, data.close[BIndex])
          this.volume.splice(AIndex, 0, data.volume[BIndex])
          // Incrementing AIndex because we're changing the array in-place
          AIndex++
          BIndex++
        } else if (ADate < BDate) {
          AIndex++
        } else {
          console.warn('fetchNewerData merge warning')
          window.Sentry?.captureMessage('quote.ts fetchNewerData merge warning', {
            extra: {
              thisDateLength: this.date.length,
              dataDateLength: data.date.length,
              AIndex,
              BIndex,
              ADate,
              BDate,
            },
          })
        }
      }

      newQuoteData.date = this.date
      newQuoteData.open = this.open
      newQuoteData.high = this.high
      newQuoteData.low = this.low
      newQuoteData.close = this.close
      newQuoteData.volume = this.volume
    }

    newQuoteData.updateOhlcVersion = data.updateOhlcVersion

    const hasUpdatedSuccessfully = this.updateAttributes(newQuoteData)
    // https://github.com/finvizhq/charts/issues/510
    if (!hasUpdatedSuccessfully) {
      // attempt to find if this.model.quote() => null is comming from this.save() validation fail
      window.Sentry?.captureMessage('quote.ts fetchNewerData save() validation failed', { extra: this })
    }
    if (!this) {
      // or this somehow became null / falsy
      window.Sentry?.captureMessage('quote.ts fetchNewerData this is falsy', { extra: this })
    }
    this.trigger(fetchType, this, fetchType)
  }

  async getResolvedFetchingPromise() {
    try {
      return await this.fetchingPromise
    } catch {
      // do nothing
    }
  }

  fetchOlderData(options?: QuoteFetchOptions) {
    return this.fetchData({ fetchType: QuoteFetchType.Older, options })
  }

  dataChangeRefetch(abortController?: null | AbortController) {
    return this.fetchData({
      fetchType: QuoteFetchType.DataChangeRefetch,
      abortController,
      options: {
        dateFrom: this.date[0],
      },
    })
  }

  load(atts: ObjectHash) {
    super.load(atts)
    // This has to be here to prevent recalculations if atts.fetchedAt is not provided
    const fetchedAt = atts.fetchedAt ?? this.fetchedAt ?? 0
    const { cachedAt } = (getCachedQuote({ cid: this.cid }) as { cachedAt: number } | null) || {}
    if (this.timeframe && cachedAt !== fetchedAt) {
      setCachedQuote({ cid: this.cid, key: 'cachedAt', value: fetchedAt, fetchedAt })
      this.isIntraday = [
        TIMEFRAME.i1,
        TIMEFRAME.i2,
        TIMEFRAME.i3,
        TIMEFRAME.i5,
        TIMEFRAME.i10,
        TIMEFRAME.i15,
        TIMEFRAME.i30,
        TIMEFRAME.h,
        TIMEFRAME.h2,
        TIMEFRAME.h4,
      ].includes(this.timeframe)
      this.interval = this.isIntraday
        ? IntradayTimeframeInterval[this.timeframe as keyof typeof IntradayTimeframeInterval]
        : 0
      this.drawDays = this.getDrawDays()
      this.barIndex = this.getBarIndexes()
      this.patterns = this.patterns ?? []
      this.barToDataIndex = this.barIndex.flatMap((barIndex, index) => {
        const lastIndex = this.barIndex[index - 1] ?? -1
        return [...Array.from({ length: barIndex - lastIndex - 1 }).fill(Math.max(0, index - 1)), index] as number[]
      })
      this.alignAllFinancialAttachmentsDataWithQuoteDates()
    }
    if (this.timeframe && this.ticker) {
      this.chartEvents = getChartEventsWithId({
        updateOhlcVersion: this.updateOhlcVersion,
        ticker: this.ticker,
        chartEvents: this.chartEvents as unknown as ChartEventDataRaw[],
      })
    }

    this.fetchingState ??= []

    return this
  }

  alignAllFinancialAttachmentsDataWithQuoteDates() {
    this.alignedFinancialAttachmentsData = {}
    this.timestampToFinancialsIndexesMap = {}
    this.financialAttachments?.forEach((quoteFinancialAttachment) => {
      this.alignFinancialAttachmentsDataWithQuoteDates(quoteFinancialAttachment)
    })
  }

  alignFinancialAttachmentsDataWithQuoteDates<T extends QuoteFinancialAttachmentType>(quoteFinancialAttachment: T) {
    const timestampToFinancialsIndexesMap: ObjectHash<number[]> = {}
    const financialAttachmentsData = this.financialAttachmentsData?.[quoteFinancialAttachment]

    if (financialAttachmentsData) {
      let lastDateIndex = 0
      const firstDate = this.date[0]
      this.alignedFinancialAttachmentsData[quoteFinancialAttachment] = financialAttachmentsData.map((data, index) => {
        let matchingQuoteTimestamp: number | null = null
        let isVirtualMatch = false

        const parsedDate = dateFromUnixTimestamp(data.timestamp)
        const convertedDateToNyTimestamp = convertLocalToNyTime(parsedDate, false).getTime() / 1000

        if (firstDate < convertedDateToNyTimestamp) {
          let previousDailyTimestamp: number | null = null
          for (let dateIndex = lastDateIndex; dateIndex < this.date.length; dateIndex += 1) {
            const timestamp = this.date[dateIndex]
            const dateDate = dateFromUnixTimestamp(timestamp)
            if (this.isIntraday || this.timeframe === TIMEFRAME.d) {
              const isBetweenDates =
                previousDailyTimestamp !== null && previousDailyTimestamp < data.timestamp && timestamp > data.timestamp
              const areDatesSameDay = isSameDay(parsedDate, dateDate)
              if (areDatesSameDay || isBetweenDates) {
                lastDateIndex = dateIndex
                matchingQuoteTimestamp = timestamp
                isVirtualMatch = isBetweenDates && !areDatesSameDay
                break
              } else {
                previousDailyTimestamp = timestamp
              }
            } else if (this.timeframe === TIMEFRAME.w) {
              if (isSameWeek(parsedDate, dateDate, { weekStartsOn: 1 })) {
                lastDateIndex = dateIndex
                matchingQuoteTimestamp = timestamp
                break
              }
            } else if (this.timeframe === TIMEFRAME.m) {
              if (isSameMonth(parsedDate, dateDate)) {
                lastDateIndex = dateIndex
                matchingQuoteTimestamp = timestamp
                break
              }
            }
          }
        }

        if (matchingQuoteTimestamp !== null) {
          const key = matchingQuoteTimestamp.toString()
          timestampToFinancialsIndexesMap[key] ??= []
          timestampToFinancialsIndexesMap[key].push(index)
        }

        return { ...data, matchingQuoteTimestamp, isVirtualMatch }
      }) as FinancialAttachmentsData[T]

      this.timestampToFinancialsIndexesMap[quoteFinancialAttachment] = timestampToFinancialsIndexesMap
    }
  }

  clearCachedData() {
    deleteCachedQuote({ cid: this.cid })
  }

  destroy(options?: ObjectHash) {
    super.destroy(options)
    this.clearCachedData()
    return this
  }

  getDrawDays() {
    if (this.instrument !== Instrument.Stock) {
      return Infinity
    }
    switch (this.timeframe) {
      case TIMEFRAME.i1:
      case TIMEFRAME.i2:
      case TIMEFRAME.i3:
      case TIMEFRAME.i5:
      case TIMEFRAME.i10:
        return 10
      case TIMEFRAME.i15:
      case TIMEFRAME.i30:
      case TIMEFRAME.h:
      case TIMEFRAME.h2:
      case TIMEFRAME.h4:
        return 15
      default:
        break
    }
  }

  getHeikinAshiData() {
    const cachedHeikinAshiData = getCachedQuote({ cid: this.cid, key: 'heikinAshiData', fetchedAt: this.fetchedAt })
    if (cachedHeikinAshiData) {
      return cachedHeikinAshiData as unknown as { open: number[]; close: number[] }
    }

    const HAClose = this.open.map((_, i) => (this.open[i] + this.high[i] + this.close[i] + this.low[i]) / 4)
    const HAOpen = this.open.reduce(
      (acc, open, i) => [...acc, (i === 0 ? open + HAClose[0] : acc[i - 1] + HAClose[i - 1]) / 2],
      [] as number[]
    )

    const heikAshiData = { open: HAOpen, close: HAClose }

    setCachedQuote({ cid: this.cid, key: 'heikinAshiData', value: heikAshiData, fetchedAt: this.fetchedAt })

    return heikAshiData
  }

  getDayToOffset(): ObjectHash<number> {
    const cachedDayToOffset = getCachedQuote({ cid: this.cid, key: 'dayToOffset', fetchedAt: this.fetchedAt })
    if (cachedDayToOffset) {
      return cachedDayToOffset as unknown as ObjectHash<number>
    }
    const dayToOffset: ObjectHash = {}
    let day = 0
    let lastDay
    for (let i = 0, end = this.date.length, asc = end >= 0; asc ? i < end : i > end; asc ? i++ : i--) {
      const date = dateFromUnixTimestamp(this.date[i])
      const dateString = dateStringFromDate(date)
      if (dateString !== lastDay) {
        dayToOffset[dateString] = day
        day++
        lastDay = dateString
      }
    }
    // for ofscreen renderer we need to add lastDate to have correct dayToOffset because if
    // premarket=false in fetch query there isn't other way to distinguish that
    if (getIsSSr() && this.lastDate) {
      // YYYYMMDD / 20211118 => YYYY-MM-DD
      const [yyyy, mm, dd] = [~~(this.lastDate / 10000), ~~(this.lastDate / 100) % 100, this.lastDate % 100]
      const lastDate = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`

      if (lastDay !== lastDate) {
        dayToOffset[lastDate] = day
      }
    }

    setCachedQuote({ cid: this.cid, key: 'dayToOffset', value: dayToOffset, fetchedAt: this.fetchedAt })
    return dayToOffset
  }

  getDaysInQuote() {
    const cachedDayToOffset = getCachedQuote({ cid: this.cid, key: 'daysInQuote', fetchedAt: this.fetchedAt })
    if (cachedDayToOffset) {
      return cachedDayToOffset as unknown as Date[]
    }
    const dayToOffset = this.getDayToOffset()
    const daysInQuote = Object.keys(dayToOffset)
      .map((dateString) => {
        const [year, month, day] = dateString.split('-')
        return new Date(Number.parseInt(year), Number.parseInt(month) - 1, Number.parseInt(day))
      })
      .sort((a, b) => a.getTime() - b.getTime())

    setCachedQuote({ cid: this.cid, key: 'daysInQuote', value: daysInQuote, fetchedAt: this.fetchedAt })
    return daysInQuote
  }

  getBarIndexes() {
    if (!this.isIntraday || this.instrument !== Instrument.Stock) {
      return Array.from({ length: this.date.length }).map((_, index) => index)
    }

    const barIndex = []
    const dayToOffset = this.getDayToOffset()

    for (let i = 0; i < this.date.length; i += 1) {
      const date = dateFromUnixTimestamp(this.date[i])
      let x = Math.ceil((date.getHours() * 60 + date.getMinutes() - this.marketStartMinutes) / this.interval)
      x += dayToOffset[dateStringFromDate(date)] * this.getBarsInDay() // add days
      barIndex[i] = x
    }

    return barIndex
  }

  getBarIndex(i: number) {
    if (!this.isIntraday || this.instrument !== Instrument.Stock) {
      return i
    }
    return this.barIndex[i]
  }

  getBarsInDay() {
    if (!this.isIntraday || this.instrument !== Instrument.Stock || !this.drawMinutesPerDay || !this.interval) {
      return 0
    }
    return Math.ceil(this.drawMinutesPerDay / this.interval)
  }

  getDataIndexByBarIndex(index: number) {
    if (this.barIndex) {
      for (let i = 0; i < this.barIndex.length; i++) {
        if (this.barIndex[i] === index) {
          return i
        }
      }
    }
    return -1
  }

  getDataByBarIndex(key: keyof Quote, index: number) {
    const dataIndex = this.getDataIndexByBarIndex(index)
    if (dataIndex > -1) {
      const quoteData = this[key as keyof Quote]! as number[]
      return quoteData[dataIndex] ?? null
    }
    return null
  }

  getDateToIndex() {
    const cachedDateToIndex = getCachedQuote({ cid: this.cid, key: 'dateToIndex', fetchedAt: this.fetchedAt })
    if (cachedDateToIndex) {
      return cachedDateToIndex as unknown as ObjectHash<number>
    }
    const dateToIndex: ObjectHash<number> = {}
    for (let i = 0; i < this.date.length; i++) {
      dateToIndex[this.date[i]] = i
    }
    setCachedQuote({ cid: this.cid, key: 'dateToIndex', value: dateToIndex, fetchedAt: this.fetchedAt })
    return dateToIndex
  }

  getTimestampFomPositionX(positionX: number) {
    if (this.date.length === 0) {
      return 0
    }
    return getTimestampFromPositionX({
      positionX,
      quote: this,
    })
  }

  getPositionXFromTimestamp(timestamp: number) {
    if (this.date.length === 0) {
      return 0
    }
    return getPositionXFromTimestamp({
      timestamp,
      quote: this,
    })
  }

  getUrl(options: QuoteUrlOptions) {
    const config: UrlOptions = {
      ticker: this.ticker,
      instrument: this.instrument,
      timeframe: this.timeframe || 'd1',
      aftermarket: this.aftermarket,
      premarket: this.premarket,
      patterns: this.hasPatterns,
      events: this.events == null ? this.instrument === Instrument.Stock : this.events,
      ...options,
      financialAttachments: this.financialAttachments?.join(','),
    }

    const isBrowserElite = !getIsSSr() && document.location.host === 'elite.finviz.com'
    let domain = isBrowserElite ? 'https://api.finviz.com' : ''

    if (getIsSSr()) {
      domain = process.env.BE_API_URL!
    }

    return `${domain}/api/quote.ashx?${queryString.stringify({ rev: Date.now(), ...config })}`
  }

  getRawTicker() {
    return getRawTickerForInstrument(this.instrument, this.ticker)
  }

  clearData() {
    this.updateAttributes(getEmptyQuote())
  }

  toConfig<T extends Partial<RootChartConfigObject> = RootChartConfigObject>(omitKeys = [] as string[]): T {
    return JSON.parse(JSON.stringify(omit(this.toJSON(), [...Quote.computableKeys, ...omitKeys])))
  }
}

Quote.initClass()

export default Quote
